import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { EntryPoint, EntryPointService, MagnetService, ProjectFilter, ProjectService } from '@mirage-projects/cw-commons';
import {
    EPSG,
    EpsgAdapterService,
    PcvizJobProcessData,
    Project,
    ProjectAdapterService,
    ProjectData,
    ResultPage,
    isDemoProject,
} from '@mirage-projects/cw-models';

@Injectable({
    providedIn: 'root',
})
export class AppProjectService implements ProjectService {
    constructor(
        private http: HttpClient,
        private projectAdapter: ProjectAdapterService,
        private entryPointService: EntryPointService,
        private epsgAdapter: EpsgAdapterService,
        private magnetService: MagnetService
    ) {}

    private projectsUrl = this.entryPointService.getResource(EntryPoint.PROJECTS);

    /**
     * Creates a new project
     * @param projectData New project data
     */
    create(projectData: ProjectData): Observable<Project> {
        return this.http
            .post<Project>(`${this.projectsUrl}`, projectData)
            .pipe(map((response: any): Project => this.projectAdapter.adapt(response)));
    }

    /**
     * Gets project by id
     * @param id Project id
     */
    get(id: number): Observable<Project> {
        const url = this.entryPointService.getResource(EntryPoint.PROJECTS, `${id}`);
        return this.http.get<Project>(`${url}`).pipe(map((response: any): Project => this.projectAdapter.adapt(response)));
    }

    /**
     * Gets the list of projects
     * @param params Parameters to filter the list
     * @returns
     */
    getProjects(params: any): Observable<ResultPage<Project>> {
        const config = { params: params };
        return this.http.get<ResultPage<Project>>(`${this.projectsUrl}`, config);
    }

    /**
     * Gets the projects page
     * @param projectFilter Parameters to filter the list
     */
    getProjectsPage(projectFilter: ProjectFilter): Observable<ResultPage<Project>> {
        const config = { params: projectFilter?.getQueryParams() };
        return this.http.get<ResultPage<Project>>(`${this.projectsUrl}`, config);
    }

    /**
     * Updates a project
     * @param projectId The project id to update
     * @param projectData The data to update
     */
    update(projectId: number, projectData: any): Observable<Project> {
        const url = this.entryPointService.getResource(EntryPoint.PROJECTS, `${projectId}`);

        return this.http.patch(`${url}`, projectData).pipe(map((response: any) => this.projectAdapter.adapt(response)));
    }

    /**
     * Deletes a project
     * @param project The project to delete
     * @param deleteOrphanLayers Set if the orphan layers should be deleted
     */
    delete(project: Project, deleteOrphanLayers?: boolean): Observable<any> {
        let url = this.entryPointService.getResource(EntryPoint.PROJECTS, `${project.id}`);
        if (deleteOrphanLayers) {
            url = `${url}?delete_orphan_layers=true`;
        }
        return this.http.delete(`${url}`).pipe(map((response: any) => response.data));
    }

    /**
     * Adds a layer to a project
     * @param projectId Project id
     * @param layerId Layer id
     */
    addLayer(projectId: number, layerId: number): Observable<any> {
        const layerData = {
            layer: layerId,
        };
        return this.http.post<any>(`${this.projectsUrl}${projectId}/layers/`, layerData);
    }

    /**
     * Adds an array of layers to a project
     * @param projectId Project id
     * @param layerIds Array of layers id
     */
    addLayers(projectId: number, layerIds: number[]): Observable<any> {
        return this.http.post<any>(`${this.projectsUrl}${projectId}/layers/`, { layerIds });
    }

    /**
     * Removes a layer from a project
     * @param projectId Project id
     * @param layerId Layer id
     * @returns
     */
    removeLayer(projectId: number, layerId: number): Observable<any> {
        return this.http.delete<Project>(`${this.projectsUrl}${projectId}/layers/${layerId}/`);
    }

    /**
     * Searchs EPSG
     * @param searchValue The search value
     * @param page The page number
     */
    searchEPSG(searchValue: string, page?: number): Observable<ResultPage<EPSG>> {
        const pageNumber = page ? `&page=${page}` : '';
        const epsgServer = this.magnetService.getEpsgServer();
        return this.http
            .get(`${epsgServer}/?format=json&q=${searchValue}${pageNumber}`)
            .pipe(map((response): ResultPage<EPSG> => this.epsgAdapter.adapt({ ...response, currentPage: page || 1 })));
    }

    /**
     * Checks if the project is in the restricted shared state
     * @param projectId Project's id
     */
    isRestrictedSharedProject(projectId: number): Observable<boolean> {
        if (!this.magnetService.isConnectingFromMagnet()) {
            return of(false);
        }

        if (projectId === 1) {
            return of(false);
        }

        return this.http.get<boolean>(`${this.projectsUrl}${projectId}/share?code=true`).pipe(map((response: any) => response.shared));
    }

    /**
     * Removes restricted shared state for the passed project as parameter
     * @param projectId Project's id
     */
    unshareRestrictedProject(projectId: number): Observable<void> {
        if (isDemoProject(projectId)) {
            return of(undefined);
        }

        const params = {
            shared: false,
        };

        return this.http.put<void>(`${this.projectsUrl}${projectId}/share?code=true`, params).pipe(map((response: any) => response.code));
    }

    /**
     * Gets the restricted user code for the get shared project link
     */
    getRestrictedUserCode(projectId: number, queryParam: string[], params: Record<string, unknown>): Observable<string> {
        const baseUrl = `${this.entryPointService.getResource(EntryPoint.PROJECTS, String(projectId))}share?code=true`;
        const url = [baseUrl, ...queryParam].join('&');

        return this.http.put<string>(url, params).pipe(map((response: any) => response.code));
    }

    /**
     * Gets the anonymous user code for the get shared project link
     */
    getAnonymousUserCode(): Observable<string> {
        const url = this.entryPointService.getResource(EntryPoint.ANONYMOUS_CODE) as string;
        return this.http.get<string>(url).pipe(map((response: any) => response.code));
    }

    /**
     * Removes the selected projects
     * @param projectsId the id of the projects to be deleted
     * @param flagDeleteOrphanLayers If true: delete orphan layers
     */
    deleteProjects(projectIds: number[], flagDeleteOrphanLayers?: boolean): Observable<any> {
        let url = `${this.entryPointService.getResource(EntryPoint.PROJECTS)}delete-projects`;
        if (flagDeleteOrphanLayers) {
            url = `${url}?delete_orphan_layers=true`;
        }
        return this.http.delete(url, { body: { projectIds } }).pipe(catchError((error) => throwError(() => new Error(error))));
    }

    /**
     * Gets the credit estimation for a process scan
     * @param projectId Project id
     * @param config Data to generate the estimation calculation
     */
    processScanEstimate(projectId: number, config: Partial<PcvizJobProcessData>): Observable<any> {
        const url = `${this.entryPointService.getResource(EntryPoint.PROJECTS)}${projectId}/workflows/process-scans/estimate`;
        return this.http.post(url, { ...config });
    }
}
